import React from "react";
import { isValidPhoneNumber } from "libphonenumber-js";
import { Alert, Button, Divider, Flex, Form, Typography } from "antd";
import { BOOKING_BLOCKED_MESSAGE } from "@/lib/api/openingsLocations";

import AppointmentModalReview from "./components/AppointmentReview";

import {
  FIRST_TIME_CLIENT,
  INewAppointmentArgs,
  IPathState,
  RESCHEDULE_MAIN,
  SETUP_PAYMENT,
} from "./index";
import { AppointmentHistoryData } from "@/lib/api/types";
import { AppointmentForm } from "@/lib/hooks/useAppointment";
import PhoneInput from "../PhoneInput";
import { extractErrorMessage } from "@/lib/helpers";
import { pushToDataLayer } from "@/lib/gtm";
import PoweredByOpenings from "@/app/components/Openings/PoweredByOpenings";
import { useBookingApi } from "./bookingApi";
import { servicesRequirePayment } from "./paymentRequirement";
import { useRepricedServices } from "./useRepricedServices";

const VERIFICATION_RESEND_COOLDOWN_SECONDS = 45;

interface IProps {
  appointment: AppointmentForm;
  onCreate: (args: INewAppointmentArgs) => void;
  onRouteChange: (path: IPathState, state: any) => void;
}
const LookupPhoneNumber = ({
  appointment,
  onRouteChange,
  onCreate,
}: IProps) => {
  const bookingApi = useBookingApi();
  // The route-to-payment decision must be made on the one authoritative,
  // modal-level pricing result. A cross-business lookup stays pending/error
  // until it succeeds, so neither this handler nor the button can continue on
  // the canonical menu's potentially different payment requirement.
  const pricing = useRepricedServices();
  const bookedServices = pricing.services;
  const isCreditCardRequired = servicesRequirePayment(bookedServices);

  const [phoneNumber, setPhoneNumber] = React.useState<string>();
  const [isSubmitting, setSubmitting] = React.useState(false);
  const [inlineError, setInlineError] = React.useState<string | null>(null);
  const handleSubmit = async () => {
    if (!phoneNumber || pricing.status !== "ready") return;

    setInlineError(null);

    if (!isValidPhoneNumber(phoneNumber)) {
      setInlineError("Please enter a valid phone number");
      return;
    }

    setSubmitting(true);

    try {
      const res = await bookingApi.getAppointmentHistory(
        phoneNumber,
        appointment.userId as string,
        appointment,
      );
      handleRoutingBasedOnResponse(res);
    } catch (e) {
      setInlineError(extractErrorMessage(e));
      setSubmitting(false);
    }
  };

  const handleRoutingBasedOnResponse = async (
    phoneNumberHistory: AppointmentHistoryData,
  ) => {
    if (!phoneNumber) return;
    const { upcomingAppointments, client, isNewClient } = phoneNumberHistory;

    pushToDataLayer("phone_entered", {
      source: "appointment_modal",
      is_new_ient: isNewClient,
      is_card_required: isCreditCardRequired,
    });

    if (client?.bookingBlocked) {
      setInlineError(BOOKING_BLOCKED_MESSAGE);
      setSubmitting(false);
      return;
    }

    const requiresPayment =
      isCreditCardRequired || Boolean(client?.depositRequired);

    if (isNewClient) {
      onRouteChange(FIRST_TIME_CLIENT, {
        phoneNumber,
        isCreditCardRequired: requiresPayment,
        verificationPurpose: phoneNumberHistory.verificationPurpose,
        verifyDuringAppointmentCreate:
          phoneNumberHistory.verificationPurpose?.startsWith("booking:"),
        verificationResendAvailableAt:
          Date.now() + VERIFICATION_RESEND_COOLDOWN_SECONDS * 1000,
      });
      return;
    }

    if (
      phoneNumberHistory.hasUpcomingAppointments &&
      phoneNumberHistory.upcomingAppointments.length === 0
    ) {
      onRouteChange(RESCHEDULE_MAIN, {
        phoneNumber,
        upcomingAppointments,
        client,
        hasUpcomingAppointments: phoneNumberHistory.hasUpcomingAppointments,
        isCreditCardRequired: requiresPayment,
        verificationPurpose: phoneNumberHistory.verificationPurpose,
      });
      return;
    }

    if (phoneNumberHistory.verificationPurpose?.startsWith("booking:")) {
      onRouteChange(FIRST_TIME_CLIENT, {
        phoneNumber,
        upcomingAppointments,
        client,
        isCreditCardRequired: requiresPayment,
        verificationPurpose: phoneNumberHistory.verificationPurpose,
        verifyDuringAppointmentCreate: true,
        verificationResendAvailableAt:
          Date.now() + VERIFICATION_RESEND_COOLDOWN_SECONDS * 1000,
      });
      return;
    }

    if ((client && client.blocked) || requiresPayment) {
      onRouteChange(SETUP_PAYMENT, {
        phoneNumber,
        upcomingAppointments,
        client,
      });
      return;
    }

    if (phoneNumberHistory.upcomingAppointments.length > 0) {
      onRouteChange(RESCHEDULE_MAIN, {
        phoneNumber,
        upcomingAppointments,
        client,
      });
      return;
    }

    onCreate({
      firstName: client ? client.firstName : "Unknown",
      lastName: client ? client.lastName : "Unknown",
      phoneNumber,
      errorCallback: (errorMessage) => {
        if (errorMessage) setInlineError(errorMessage);
        setSubmitting(false);
      },
    });
  };

  return (
    <>
      <AppointmentModalReview appointment={appointment} />
      <Form layout="vertical">
        <Form.Item
          label={<Typography.Text strong>Phone Number:</Typography.Text>}
          name="phoneNumber"
          htmlFor="appointment-phone-number"
        >
          <PhoneInput
            id="appointment-phone-number"
            name="phoneNumber"
            value={phoneNumber}
            onChange={setPhoneNumber}
            showInternationalToggle
          />
        </Form.Item>
      </Form>
      {inlineError && (
        <Alert
          type="error"
          showIcon
          message={inlineError}
          style={{ marginBottom: 8 }}
        />
      )}
      <Divider dashed />
      <Flex justify="space-between" align="center">
        <Button
          onClick={handleSubmit}
          disabled={
            !phoneNumber || isSubmitting || pricing.status !== "ready"
          }
          loading={isSubmitting}
          type="primary"
        >
          Submit
        </Button>
        <PoweredByOpenings />
      </Flex>
    </>
  );
};

export default LookupPhoneNumber;
